import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "motion/react";
import AppShell from "@/components/AppShell";
import MapPreview, { type BoundaryInput, type MapPreviewHandle } from "@/components/MapPreview";
import RasterPanel from "@/components/RasterPanel";
import FeatureTable from "@/components/FeatureTable";
import HierarchyImportPanel from "@/components/HierarchyImportPanel";
import BlockControls from "@/components/BlockControls";
import {
  Banner,
  BlockStatusBadge,
  Button,
  EmptyState,
  Panel,
  Spinner,
  StatTile,
  StatusDistribution,
  type DistributionSegment,
} from "@/components/ui";
import { STAGGER_LIST, STAGGER_ITEM } from "@/lib/utils";
import {
  createBlock,
  createWard,
  createZone,
  errorDetail,
  getProject,
  getSurveyPackage,
  listBlocks,
  listFeatures,
  listWards,
  listZones,
} from "@/lib/api";
import { getStoredUser } from "@/lib/auth";
import type {
  BlockStatus,
  FieldFeature,
  Project,
  Raster,
  SurveyBlock,
  Ward,
  Zone,
} from "@/lib/types";
import { useLanguage } from "@/i18n";
import { DISPLAY_TIME_ZONE, formatDateTime, parseApiDate } from "@/lib/format";

export default function HierarchyPage() {
  // react-router hands params straight off the URL as strings. Projects moved
  // to integer primary keys, so the id is parsed once here rather than at each
  // call site. A URL like /projects/abc yields NaN; the effects below then fetch
  // nothing and the page stays empty rather than sending "NaN" to the API.
  const { projectId: projectIdParam = "" } = useParams<{ projectId: string }>();
  const projectId = Number(projectIdParam);
  const { t, lang } = useLanguage();

  const [project, setProject] = useState<Project | null>(null);
  const [zones, setZones] = useState<Zone[]>([]);
  const [wards, setWards] = useState<Ward[]>([]);
  const [blocks, setBlocks] = useState<SurveyBlock[]>([]);
  const mapRef = useRef<MapPreviewHandle>(null);

  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const [selectedWard, setSelectedWard] = useState<Ward | null>(null);
  const [selectedBlock, setSelectedBlock] = useState<SurveyBlock | null>(null);

  const [blockRasters, setBlockRasters] = useState<Raster[]>([]);
  const [blockFeatures, setBlockFeatures] = useState<FieldFeature[]>([]);

  // Staff and admin correct submitted data in place; the backend's
  // assert_block_mutable exempts them. Read once -- the signed-in account does
  // not change without a reload. Matches PRIVILEGED_ROLES server-side, spelled
  // out rather than "!== surveyor" so an unrecognised role is treated as the
  // restricted case.
  const isOfficeUser = useMemo(() => {
    const role = getStoredUser()?.role;
    return role === "admin" || role === "staff";
  }, []);
  // Project-scoped, not block-scoped -- managed from the project workspace
  // page, but shown here too since a reference overlay is context for the
  // whole project's map, not just the one you happen to be uploading imagery
  // from. Fetched once for the project rather than re-fetched per block.

  const [newZoneName, setNewZoneName] = useState("");
  const [newWardName, setNewWardName] = useState("");
  const [newBlockName, setNewBlockName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshZones = useCallback(() => {
    listZones(projectId).then(setZones).catch((e) => setError(String(e)));
  }, [projectId]);

  useEffect(() => {
    getProject(projectId).then(setProject).catch(() => {});
    refreshZones();
  }, [projectId, refreshZones]);

  function selectZone(zone: Zone) {
    setSelectedZone(zone);
    setSelectedWard(null);
    setSelectedBlock(null);
    setWards([]);
    setBlocks([]);
    listWards(zone.id).then(setWards).catch((e) => setError(String(e)));
  }

  function selectWard(ward: Ward) {
    setSelectedWard(ward);
    setSelectedBlock(null);
    setBlocks([]);
    listBlocks(ward.id).then(setBlocks).catch((e) => setError(String(e)));
  }

  /// Re-reads the selected ward's blocks. Needed after a feature deletion as
  /// well as after adding a block: deleting a submitted block's last shape
  /// makes the backend un-submit it, which changes the status badges, the
  /// ward progress tiles and the Completed surveys list all at once.
  const refreshBlocks = useCallback(() => {
    if (!selectedWard) return;
    listBlocks(selectedWard.id).then(setBlocks).catch((e) => setError(String(e)));
  }, [selectedWard]);

  const refreshBlockDetail = useCallback((blockId: number) => {
    // One call, and deliberately the same one the field app makes -- see
    // getSurveyPackage. It returns block-scoped AND project-wide imagery, so
    // the portal shows the imagery a surveyor would actually be offered here.
    getSurveyPackage(blockId)
      .then((pkg) => {
        setBlockRasters(pkg.rasters);
        setBlockFeatures(pkg.features);
        // pkg.block carries the block's *current* status, so this also keeps
        // the badge in the block-detail header honest when a deletion has
        // just reverted a submission underneath it.
        setSelectedBlock(pkg.block);
        // Keep the "Shapes" column in the Completed surveys table (below) in
        // sync with deletions made right here in this block's own
        // FeatureTable. The shapeCounts effect only recomputes when the set
        // of *submitted blocks* changes -- deleting a feature doesn't touch
        // that array, so without this the count stayed stale until the next
        // full page load. pkg.features is fetched with the identical
        // block_id filter the shapeCounts effect itself uses, so its length
        // is exactly the number that effect would land on.
        setShapeCounts((prev) => ({ ...prev, [blockId]: pkg.features.length }));
      })
      .catch((e) => setError(String(e)));
  }, []);

  function selectBlock(block: SurveyBlock) {
    setSelectedBlock(block);
    refreshBlockDetail(block.id);
  }

  async function addZone(e: React.FormEvent) {
    e.preventDefault();
    if (!newZoneName.trim()) return;
    setBusy(true);
    try {
      await createZone({ project_id: projectId, name: newZoneName.trim() });
      setNewZoneName("");
      refreshZones();
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  }

  async function addWard(e: React.FormEvent) {
    e.preventDefault();
    if (!newWardName.trim() || !selectedZone) return;
    setBusy(true);
    try {
      await createWard({ zone_id: selectedZone.id, name: newWardName.trim() });
      setNewWardName("");
      setWards(await listWards(selectedZone.id));
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  }

  async function addBlock(e: React.FormEvent) {
    e.preventDefault();
    if (!newBlockName.trim() || !selectedWard) return;
    setBusy(true);
    try {
      await createBlock({ ward_id: selectedWard.id, name: newBlockName.trim() });
      setNewBlockName("");
      setBlocks(await listBlocks(selectedWard.id));
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  }

  // Ward-level progress, plus the submissions list -- the office counterpart to
  // the mobile "Submit survey" action.
  const statusCounts = useMemo(() => {
    const counts: Record<BlockStatus, number> = { pending: 0, in_progress: 0, submitted: 0 };
    for (const b of blocks) if (b.status in counts) counts[b.status] += 1;
    return counts;
  }, [blocks]);

  const segments: DistributionSegment[] = [
    { key: "submitted", label: t.hierarchyPage.submitted, count: statusCounts.submitted, tone: "good" },
    { key: "in_progress", label: t.hierarchyPage.inProgress, count: statusCounts.in_progress, tone: "warning" },
    { key: "pending", label: t.hierarchyPage.notStarted, count: statusCounts.pending, tone: "neutral" },
  ];

  const submitted = useMemo(
    () =>
      blocks
        .filter((b) => b.status === "submitted")
        .sort((a, b) => (b.submitted_at ?? "").localeCompare(a.submitted_at ?? "")),
    [blocks]
  );

  // The "Shapes" column used to read b.raster_count -- the block's *imagery*
  // file count, not the number of points/polygons actually collected. It
  // went unnoticed because in the original test data both happened to be
  // small numbers; the mismatch became obvious once a block had several
  // rasters (including a retried/re-uploaded one) but zero collected shapes.
  // There's no bulk endpoint for this, so it's one request per submitted
  // block -- fine at the scale a "completed surveys" list for one ward
  // actually reaches.
  const [shapeCounts, setShapeCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    let cancelled = false;
    Promise.all(
      submitted.map((b) =>
        listFeatures({ blockId: b.id })
          .then((features) => [b.id, features.length] as const)
          .catch(() => [b.id, -1] as const) // -1: couldn't load, shown as "—"
      )
    ).then((pairs) => {
      if (!cancelled) setShapeCounts(Object.fromEntries(pairs));
    });
    return () => {
      cancelled = true;
    };
  }, [submitted]);

  const mapBoundaries: BoundaryInput[] = useMemo(() => {
    const out: BoundaryInput[] = [];
    if (selectedZone?.boundary) {
      out.push({
        id: selectedZone.id,
        label: selectedZone.name,
        geometry: selectedZone.boundary,
        kind: "zone",
      });
    }
    if (selectedWard?.boundary) {
      out.push({
        id: selectedWard.id,
        label: selectedWard.name,
        geometry: selectedWard.boundary,
        kind: "ward",
      });
    }
    // Once a block is picked, draw ONLY that block. Previously every block in
    // the ward was drawn regardless, so selecting one block still produced a
    // map covered in six outlines and there was no way to see which one was
    // actually selected -- the point of selecting it.
    //
    // With no block selected, all of them are still drawn: that is the overview
    // you are choosing from, and dropping it would leave the map empty while
    // browsing a ward.
    const blocksToDraw = selectedBlock
      ? blocks.filter((b) => b.id === selectedBlock.id)
      : blocks;
    for (const b of blocksToDraw) {
      if (b.boundary) {
        out.push({ id: b.id, label: b.name, geometry: b.boundary, kind: "block" });
      }
    }
    return out;
  }, [selectedZone, selectedWard, selectedBlock, blocks]);

  return (
    <AppShell
      crumbs={[
        { label: t.sidebar.projects, href: "/" },
        { label: project?.name ?? "…", href: `/projects/${projectId}` },
        { label: t.hierarchyPage.crumbZonesWardsBlocks },
      ]}
    >
      <div className="flex min-h-0 flex-col gap-3 p-4">
        {error && (
          <Banner tone="danger" action={<Button size="sm" onClick={() => setError(null)}>{t.common.dismiss}</Button>}>
            {error}
          </Banner>
        )}

        {/* Bulk import sits above the per-level "Add" forms: typing zones,
            wards and blocks in one at a time is only reasonable for a handful,
            and none of it can carry boundary geometry -- which is what the
            field app needs to scope an offline download. */}
        <HierarchyImportPanel
          projectId={projectId}
          refreshKey={`${zones.length}-${wards.length}-${blocks.length}`}
          onImported={() => {
            // A zone import changes the zone list; a ward or block import
            // changes whatever is currently selected below it. Refreshing all
            // three is one small request each and avoids guessing which level
            // was just imported.
            refreshZones();
            if (selectedZone) {
              listWards(selectedZone.id).then(setWards).catch((e) => setError(String(e)));
            }
            refreshBlocks();
          }}
        />

        {/* Three-column picker */}
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <HierarchyColumn
            title={t.hierarchyPage.zonesTitle}
            items={zones.map((z) => ({ id: z.id, label: z.name, hasGeometry: !!z.boundary }))}
            selectedId={selectedZone?.id}
            onSelect={(id) => {
              const z = zones.find((x) => x.id === id);
              if (z) selectZone(z);
            }}
            onCreate={addZone}
            value={newZoneName}
            onValueChange={setNewZoneName}
            placeholder={t.hierarchyPage.zonePlaceholder}
            busy={busy}
          />
          <HierarchyColumn
            title={t.hierarchyPage.wardsTitle}
            items={wards.map((w) => ({ id: w.id, label: w.name, hasGeometry: !!w.boundary }))}
            selectedId={selectedWard?.id}
            onSelect={(id) => {
              const w = wards.find((x) => x.id === id);
              if (w) selectWard(w);
            }}
            onCreate={addWard}
            value={newWardName}
            onValueChange={setNewWardName}
            placeholder={t.hierarchyPage.wardPlaceholder}
            disabled={!selectedZone}
            emptyHint={t.hierarchyPage.selectZoneFirst}
            busy={busy}
          />
          <HierarchyColumn
            title={t.hierarchyPage.blocksTitle}
            items={blocks.map((b) => ({
              id: b.id,
              label: b.name,
              hasGeometry: !!b.boundary,
              status: b.status,
            }))}
            selectedId={selectedBlock?.id}
            onSelect={(id) => {
              const b = blocks.find((x) => x.id === id);
              if (b) selectBlock(b);
            }}
            onCreate={addBlock}
            value={newBlockName}
            onValueChange={setNewBlockName}
            placeholder={t.hierarchyPage.blockPlaceholder}
            disabled={!selectedWard}
            emptyHint={t.hierarchyPage.selectWardFirst}
            busy={busy}
          />
        </div>

        {/* Ward progress + submissions */}
        {selectedWard && blocks.length > 0 && (
          <motion.div
            className="grid grid-cols-1 gap-3 lg:grid-cols-[repeat(3,minmax(0,1fr))_2fr]"
            variants={STAGGER_LIST}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={STAGGER_ITEM}>
              <StatTile label={t.hierarchyPage.blocksInWard} value={blocks.length} />
            </motion.div>
            <motion.div variants={STAGGER_ITEM}>
              <StatTile
                label={t.hierarchyPage.submitted}
                value={statusCounts.submitted}
                tone={statusCounts.submitted === blocks.length ? "good" : "neutral"}
              />
            </motion.div>
            <motion.div variants={STAGGER_ITEM}>
              <StatTile label={t.hierarchyPage.inProgress} value={statusCounts.in_progress} tone="warning" />
            </motion.div>
            <motion.div variants={STAGGER_ITEM}>
              <StatusDistribution
                segments={segments}
                total={blocks.length}
                caption={selectedWard.name}
              />
            </motion.div>
          </motion.div>
        )}

        {selectedWard && (
          <Panel title={t.hierarchyPage.completedSurveys(submitted.length)}>
            {submitted.length === 0 ? (
              <EmptyState
                title={t.hierarchyPage.nothingSubmittedYet}
                body={t.hierarchyPage.nothingSubmittedBody}
              />
            ) : (
              <table className="w-full border-collapse text-left text-xs">
                <thead className="bg-surface-sunken text-2xs uppercase tracking-wider text-ink-muted">
                  <tr>
                    <th className="px-4 py-2 font-semibold">{t.hierarchyPage.colBlock}</th>
                    <th className="px-4 py-2 font-semibold">{t.hierarchyPage.colSubmittedBy}</th>
                    <th className="px-4 py-2 font-semibold">{t.hierarchyPage.colSubmitted}</th>
                    <th className="px-4 py-2 font-semibold">{t.hierarchyPage.colClaimedBy}</th>
                    <th className="px-4 py-2 text-right font-semibold">{t.hierarchyPage.colShapes}</th>
                  </tr>
                </thead>
                <tbody>
                  {submitted.map((b) => (
                    <tr key={b.id} className="border-t border-line hover:bg-surface-sunken/60">
                      <td className="px-4 py-2 font-medium text-ink">{b.name}</td>
                      <td className="px-4 py-2 text-ink-secondary">
                        {/* The field app sends "username (uuid)"; show the name
                            prominently and keep the id available but quiet. */}
                        {b.submitted_by ? <SubmittedBy value={b.submitted_by} /> : "—"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-ink-secondary">
                        {b.submitted_at
                          ? parseApiDate(b.submitted_at).toLocaleString("en-IN", {
                              timeZone: DISPLAY_TIME_ZONE,
                              day: "2-digit",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "—"}
                      </td>
                      <td className="px-4 py-2 text-ink-secondary">{b.assigned_surveyor || "—"}</td>
                      <td className="tabular px-4 py-2 text-right text-ink-secondary">
                        {shapeCounts[b.id] === undefined ? (
                          <Spinner className="inline-block h-3 w-3" />
                        ) : shapeCounts[b.id] === -1 ? (
                          "—"
                        ) : (
                          shapeCounts[b.id]
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        )}

        {/* Selected block detail */}
        {selectedBlock && (
          <>
            <div className="flex flex-wrap items-center gap-3 rounded-md bg-surface px-4 py-2.5 shadow-ring">
              <span className="text-sm font-semibold text-ink">{selectedBlock.name}</span>
              <BlockStatusBadge status={selectedBlock.status} />
              {!selectedBlock.boundary && (
                <span className="text-xs text-status-text-critical">
                  {t.hierarchyPage.noBoundaryWarning}
                </span>
              )}
            </div>

            {/* Who holds the block and since when, plus Release / Reassign /
                Send back (reason + validity required) and the block's history.
                See components/BlockControls.tsx. */}
            <BlockControls
              block={selectedBlock}
              isOfficeUser={isOfficeUser}
              onChanged={() => {
                refreshBlockDetail(selectedBlock.id);
                refreshBlocks();
              }}
            />

            {/* h-[26rem] (not min-h-[22rem]): unlike the project workspace
                page, this page's root has no h-full -- the whole page scrolls
                naturally as one unit, so there's no "remaining viewport
                space" for a min-height to bound against. A minimum height is
                a floor, not a ceiling, so RasterPanel just grew to fit every
                uploaded raster instead of ever scrolling internally. An
                explicit height gives it a real box to scroll inside.

                grid-rows-[minmax(0,1fr)]: even with h-[26rem] on the
                container itself, CSS Grid's default `auto` row sizing still
                sizes the row to fit content rather than respecting that
                height as a ceiling -- the same bug as the project workspace
                page's grid, just reached a different way. This forces the
                single row to be exactly the container's height and lets it
                shrink below content size, which is what actually makes
                RasterPanel's internal scroll engage instead of the whole
                card silently overflowing past h-[26rem]. */}
            <div className="grid h-[26rem] min-h-0 grid-rows-[minmax(0,1fr)] grid-cols-1 gap-3 lg:grid-cols-[1.7fr_1fr]">
              <div className="h-full min-h-0">
                <MapPreview
                  ref={mapRef}
                  rasters={blockRasters}
                  features={blockFeatures}
                  boundaries={mapBoundaries}
                />
              </div>
              <div className="h-full min-h-0">
                <RasterPanel
                  scope={{ blockId: selectedBlock.id }}
                  rasters={blockRasters}
                  onChanged={() => refreshBlockDetail(selectedBlock.id)}
                  onZoomTo={(bounds) => mapRef.current?.flyToBounds(bounds)}
                />
              </div>
            </div>

            <div className="h-64 flex-none">
              <FeatureTable
                scope={{ blockId: selectedBlock.id }}
                features={blockFeatures}
                // Staff and admin edit submitted data in place, so the lock
                // only applies to a surveyor signed into the portal. Derived
                // from the role rather than hardcoded false, so the button
                // still explains itself instead of 409ing for one.
                locked={selectedBlock.status === "submitted" && !isOfficeUser}
                lockedReason={t.hierarchyPage.featureTableLockedReason}
                onChanged={() => {
                  refreshBlockDetail(selectedBlock.id);
                  // Unconditional rather than keyed off the delete response's
                  // block_submission_reverted flag: it's one cheap request,
                  // and it keeps the badges/progress/submissions list correct
                  // no matter which mutation fired this.
                  refreshBlocks();
                }}
              />
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}

function SubmittedBy({ value }: { value: string }) {
  const match = /^(.*?)\s*\(([0-9a-f-]{8,})\)$/i.exec(value);
  if (!match) return <>{value}</>;
  return (
    <span>
      <span className="text-ink">{match[1]}</span>
      <span className="ml-1.5 font-mono text-2xs text-ink-muted">{match[2].slice(0, 8)}</span>
    </span>
  );
}

type ColumnItem = {
  // Mirrors a zone/ward/block primary key, which is an int now.
  id: number;
  label: string;
  hasGeometry: boolean;
  status?: BlockStatus;
};

/** Same order as the field-data filters: 1A, 1B, 1C, 2, 3A ... 10A, and
 *  "BLOCK NO-2" before "BLOCK NO-10". */
const naturalOrder = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

function HierarchyColumn({
  title,
  items,
  selectedId,
  onSelect,
  onCreate,
  value,
  onValueChange,
  placeholder,
  disabled = false,
  emptyHint,
  busy = false,
}: {
  title: string;
  items: ColumnItem[];
  selectedId?: number;
  onSelect: (id: number) => void;
  onCreate: (e: React.FormEvent) => void;
  value: string;
  onValueChange: (v: string) => void;
  placeholder: string;
  disabled?: boolean;
  emptyHint?: string;
  busy?: boolean;
}) {
  const { t } = useLanguage();
  return (
    <Panel title={`${title}${items.length && !disabled ? ` · ${items.length}` : ""}`} className="h-72">
      <div className="flex h-full min-h-0 flex-col">
        <ul className="thin-scroll min-h-0 flex-1 overflow-y-auto p-1.5">
          {disabled && (
            <li className="px-2 py-6 text-center text-xs text-ink-muted">{emptyHint}</li>
          )}
          {!disabled && items.length === 0 && (
            <li className="px-2 py-6 text-center text-xs text-ink-muted">
              {t.hierarchyPage.noneYet}
            </li>
          )}
          {!disabled &&
            [...items].sort((a, b) => naturalOrder.compare(a.label, b.label)).map((item) => {
              const active = selectedId === item.id;
              return (
                /* Select and delete are SIBLING buttons, not nested: the row used
                   to be a single <button>, and putting a delete control inside it
                   would be invalid HTML and unreachable by keyboard. */
                <li
                  key={item.id}
                  className={
                    "flex items-center rounded transition " +
                    (active ? "bg-accent-subtle" : "hover:bg-surface-sunken")
                  }
                >
                  <button
                    onClick={() => onSelect(item.id)}
                    aria-current={active ? "true" : undefined}
                    className={
                      "flex min-w-0 flex-1 items-center gap-2 rounded px-2.5 py-1.5 text-left text-xs transition " +
                      (active
                        ? "font-medium text-accent-press"
                        : "text-ink-secondary hover:text-ink")
                    }
                  >
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    {!item.hasGeometry && (
                      <span
                        title={t.hierarchyPage.noBoundaryGeometry}
                        className="flex-none text-2xs text-ink-muted"
                      >
                        {t.hierarchyPage.noShape}
                      </span>
                    )}
                    {item.status && <BlockStatusBadge status={item.status} />}
                  </button>
                </li>
              );
            })}
        </ul>

        {!disabled && (
          <form onSubmit={onCreate} className="flex flex-none gap-1.5 border-t border-line p-2">
            <input
              value={value}
              onChange={(e) => onValueChange(e.target.value)}
              placeholder={placeholder}
              className="h-8 min-w-0 flex-1 rounded border-0 bg-surface-sunken px-2.5 text-xs text-ink shadow-ring placeholder:text-ink-muted focus:bg-surface-raised"
            />
            <Button type="submit" size="sm" variant="secondary" disabled={busy || !value.trim()}>
              {busy ? <Spinner className="h-3 w-3" /> : t.common.add}
            </Button>
          </form>
        )}
      </div>
    </Panel>
  );
}
